// Presentation rules for the backoffice broker governance area (SM-716,
// SM-621 phase 08a). Pure functions — the pages, the tabs and the
// dialogs all read them, and the unit tests pin them — so "what may this
// operator do to this node right now", "what does this refusal mean" and
// "where does this trace step lead" are decided in exactly one place.
//
// Every rule here is broker-service's first. The lifecycle table
// (DRAFT → ACTIVE → PAUSED ⇄ ACTIVE → CLOSING → CLOSED, DRAFT → CLOSED),
// the SYSTEM_BROKER guard, the closure obligations and the staff
// membership machine are all enforced there; the console mirrors them so
// a button that would only be refused is never offered as live, and
// shows the server's refusal verbatim when it refuses anyway.

import { APIError } from "@/lib/api";
import type {
  BlockingObligation,
  BrokerAuditEventItem,
  BrokerErrorDetail,
  BrokerItem,
  BrokerStatus,
  InvestigationStep,
  LifecycleTransition,
  LifecycleTransitionRequest,
  StaffMembershipItem,
} from "@/lib/broker-types";
import { Perm, type PermCode } from "@/lib/perms";

export type Tone = "danger" | "warning" | "success" | "muted" | "accent";

/**
 * Every currency in broker-service's pool order — which, since SM-866,
 * is the SYSTEM_BROKER's list alone: it is the only node in game mode.
 * Codes are identifiers, not copy: pickers show the code.
 */
export const BROKER_CURRENCIES = ["GOLD", "RED", "GREEN"] as const;

export type BrokerCurrency = (typeof BROKER_CURRENCIES)[number];

/** What every broker node other than the SYSTEM_BROKER trades (SM-866): CASH mode, GOLD. */
export const BROKER_NODE_CURRENCIES: readonly BrokerCurrency[] = ["GOLD"];

/**
 * The currencies one node trades, in GOLD, RED, GREEN order: the admin
 * path read's `currencies` (SM-868), or — from a broker-service without
 * the field, or before the read lands — the rule it states: GOLD, RED
 * and GREEN on the SYSTEM_BROKER, GOLD on every other node. `isSystem`
 * is what the caller already knows about the node (its broker row, its
 * id), used only when the read does not say.
 */
export function nodeCurrencies(
  node: { currencies?: readonly string[]; is_system?: boolean } | null | undefined,
  isSystem = false,
): BrokerCurrency[] {
  const listed = node?.currencies;
  if (listed && listed.length > 0) return BROKER_CURRENCIES.filter((c) => listed.includes(c));
  return node?.is_system || isSystem ? [...BROKER_CURRENCIES] : [...BROKER_NODE_CURRENCIES];
}

/**
 * Exposure is external credit, which exists in GOLD only (SM-768 §3, B7),
 * and so does every receivable raised against it. broker-service refuses
 * any other currency on the exposure reads (400), so the risk views have
 * no currency to choose and the receivables filter offers this one alone.
 */
export const EXPOSURE_CURRENCY = "GOLD";

/**
 * The currencies a broker payout batch can be in — brokerv1.PayoutCurrencies.
 * No rail pays RED or GREEN (SM-708 Q4): that revenue accrues but is never
 * batched, so the payout batch filter offers these alone.
 */
export const BROKER_PAYOUT_CURRENCIES = ["GOLD"] as const;

export function isBrokerCurrency(v: unknown): v is (typeof BROKER_CURRENCIES)[number] {
  return typeof v === "string" && (BROKER_CURRENCIES as readonly string[]).includes(v);
}

// SM-866 / SM-868: broker-service refuses game mode on a broker node,
// and money in RED or GREEN into one (a pool allocation, a settlement
// note, a revenue credit, a master top-up — backoffice-service reuses the
// code for the last).
const NODE_CURRENCY_REFUSALS: Record<string, { message: string }> = {
  BROKER_TRADING_MODE_NOT_ALLOWED: {
    message: "Broker nodes trade in cash mode only. Game mode is reserved for the SYSTEM_BROKER, so only CASH can be set here.",
  },
  BROKER_CURRENCY_NOT_TRADED: {
    message:
      "This broker node trades GOLD only, so it cannot receive money in that currency. RED and GREEN belong to the SYSTEM_BROKER alone; use GOLD instead.",
  },
};

/** What a node-currency refusal means, in words; null for any other error. */
export function nodeCurrencyRefusal(e: unknown): string | null {
  return e instanceof APIError ? NODE_CURRENCY_REFUSALS[e.code]?.message ?? null : null;
}

// `label` keys so the page-copy extractor carries every word to the
// Thai catalogue (it reads UI property names, not bare map values).
const BROKER_STATUS: Record<BrokerStatus, { label: string; tone: Tone; summary: string }> = {
  DRAFT: {
    label: "Draft",
    tone: "muted",
    summary: "Awaiting exchange approval. It has no users, accounts or money yet; it can be approved or discarded.",
  },
  ACTIVE: {
    label: "Active",
    tone: "success",
    summary: "Live. New accounts, orders and pool allocations are accepted.",
  },
  PAUSED: {
    label: "Paused",
    tone: "warning",
    summary:
      "New accounts, orders and pool allocations are refused here and in every node beneath it. Users can still sign in, cancel orders, reduce positions and withdraw; settlement and payouts carry on.",
  },
  CLOSING: {
    label: "Closing",
    tone: "danger",
    summary:
      "Winding down. Nothing new is accepted, and the node closes once every obligation — descendants, balances, positions, unpaid revenue — is cleared.",
  },
  CLOSED: {
    label: "Closed",
    tone: "muted",
    summary: "Closed. The node is kept for historical attribution and nothing about it can change.",
  },
};

export function brokerStatusLabel(status: string | undefined): string {
  if (!status) return "—";
  return BROKER_STATUS[status as BrokerStatus]?.label ?? status;
}

export function brokerStatusTone(status: string | undefined): Tone {
  return BROKER_STATUS[status as BrokerStatus]?.tone ?? "muted";
}

/** A plain sentence on what the status permits. */
export function brokerStatusSummary(status: string | undefined): string {
  return BROKER_STATUS[status as BrokerStatus]?.summary ?? "";
}

/**
 * The effective status differs from the node's own one: an ancestor is
 * more restrictive, so the node behaves as that status.
 */
export function isRestrictedByAncestor(b: Pick<BrokerItem, "status" | "effective_status">): boolean {
  return Boolean(b.effective_status) && b.effective_status !== b.status;
}

/** The node's name as an operator reads it; the id when it has none. */
export function brokerName(b: Pick<BrokerItem, "broker_id" | "display_name" | "legal_name"> | undefined): string {
  if (!b) return "";
  return b.display_name || b.legal_name || b.broker_id;
}

// ── lifecycle ────────────────────────────────────────────────────────

/** The six operator verbs: five transitions and the emergency disable. */
export type LifecycleAction = "approve" | "pause" | "emergency" | "resume" | "begin-close" | "close";

export interface LifecycleActionSpec {
  transition: LifecycleTransition;
  /** The own statuses broker-service accepts the transition from. */
  from: BrokerStatus[];
  perm: PermCode;
  tone: "primary" | "danger";
  label: string;
  title: string;
  description: string;
  confirmLabel: string;
}

export const LIFECYCLE_ACTIONS: Record<LifecycleAction, LifecycleActionSpec> = {
  approve: {
    transition: "approve",
    from: ["DRAFT"],
    perm: Perm.BrokerApprove,
    tone: "primary",
    label: "Approve",
    title: "Approve this broker?",
    description:
      "The node becomes ACTIVE: it can take users, accounts and funding. Children are approved only under an approved parent.",
    confirmLabel: "Approve",
  },
  pause: {
    transition: "pause",
    from: ["ACTIVE"],
    perm: Perm.BrokerPause,
    tone: "danger",
    label: "Pause",
    title: "Pause this broker?",
    description:
      "New accounts, orders and pool allocations are refused for this node and every node beneath it. Users can still sign in, cancel, reduce positions and withdraw; settlement and payouts carry on. Resume reverses it.",
    confirmLabel: "Pause",
  },
  emergency: {
    transition: "pause",
    from: ["ACTIVE"],
    perm: Perm.BrokerPause,
    tone: "danger",
    label: "Emergency disable",
    title: "Emergency disable this broker?",
    description:
      "This is a pause recorded as an EMERGENCY in the audit trail, so it can be found and reviewed later. The effect is the same as a pause: nothing new is accepted here or beneath it, while cancels, reductions, withdrawals, settlement and payouts carry on. Resume reverses it.",
    confirmLabel: "Disable now",
  },
  resume: {
    transition: "resume",
    from: ["PAUSED"],
    perm: Perm.BrokerPause,
    tone: "primary",
    label: "Resume",
    title: "Resume this broker?",
    description:
      "The node becomes ACTIVE again. Nodes beneath it stay restricted if they, or another ancestor, are paused themselves.",
    confirmLabel: "Resume",
  },
  "begin-close": {
    transition: "begin-close",
    from: ["ACTIVE", "PAUSED"],
    perm: Perm.BrokerClose,
    tone: "danger",
    label: "Begin closure",
    title: "Begin closing this broker?",
    description:
      "The node becomes CLOSING: nothing new is accepted here or beneath it, and it can no longer be resumed or moved. It closes once every obligation is cleared.",
    confirmLabel: "Begin closure",
  },
  close: {
    transition: "close",
    from: ["CLOSING", "DRAFT"],
    perm: Perm.BrokerClose,
    tone: "danger",
    label: "Close",
    title: "Close this broker?",
    description:
      "The node becomes CLOSED for good. broker-service refuses while anything is still open — descendants, accounts, balances, positions or unpaid revenue — and lists each one with what clears it.",
    confirmLabel: "Close",
  },
};

/** The order the actions are offered in. */
export const LIFECYCLE_ACTION_ORDER: LifecycleAction[] = ["approve", "resume", "pause", "emergency", "begin-close", "close"];

/**
 * The verbs this operator may run on b now: its own status allows the
 * transition and they hold its permission. The SYSTEM_BROKER takes none
 * but approval (broker-service refuses the rest), and a CLOSED node
 * none at all.
 */
export function lifecycleActions(
  b: Pick<BrokerItem, "status" | "is_system">,
  has: (perm: PermCode) => boolean,
): LifecycleAction[] {
  return LIFECYCLE_ACTION_ORDER.filter((a) => {
    const spec = LIFECYCLE_ACTIONS[a];
    if (b.is_system && a !== "approve") return false;
    return spec.from.includes(b.status) && has(spec.perm);
  });
}

/** A DRAFT node's close discards it; the copy says so. */
export function closeDiscardsDraft(b: Pick<BrokerItem, "status">): boolean {
  return b.status === "DRAFT";
}

/**
 * The body a verb sends: the reason, the row version the operator
 * decided on (a retry after a lost response is then refused
 * BROKER_LIFECYCLE_CONFLICT rather than applied twice), and for the
 * emergency disable reason_kind EMERGENCY on a pause (D64).
 */
export function lifecycleRequest(action: LifecycleAction, reason: string, rowVersion?: number): LifecycleTransitionRequest {
  const body: LifecycleTransitionRequest = { reason: reason.trim() };
  if (rowVersion && rowVersion > 0) body.expected_row_version = rowVersion;
  if (action === "emergency") body.reason_kind = "EMERGENCY";
  return body;
}

/** An audit row written by an emergency disable. */
export function isEmergencyEvent(e: Pick<BrokerAuditEventItem, "after">): boolean {
  const after = e.after;
  return Boolean(after && typeof after === "object" && (after as Record<string, unknown>).reason_kind === "EMERGENCY");
}

// ── refusals ─────────────────────────────────────────────────────────

/** The broker error body an APIError carries, when it is one. */
export function brokerErrorDetail(e: unknown): BrokerErrorDetail | null {
  if (!(e instanceof APIError)) return null;
  const body = e.body as { error?: BrokerErrorDetail } | null;
  return body && typeof body === "object" && body.error && typeof body.error === "object" ? body.error : null;
}

/** What stands between the node and CLOSED, from a BROKER_CLOSE_BLOCKED refusal. */
export function blockingObligations(e: unknown): BlockingObligation[] {
  const detail = brokerErrorDetail(e);
  return Array.isArray(detail?.blocking) ? detail.blocking : [];
}

const OBLIGATIONS: Record<string, { label: string }> = {
  DESCENDANTS: { label: "Open descendants" },
  UNPAID_REVENUE: { label: "Unpaid revenue" },
  OPEN_ACCOUNTS: { label: "Open accounts" },
  AVAILABLE_BALANCE: { label: "Available balance" },
  RESERVED_FUNDS: { label: "Reserved funds" },
  PENDING_FUNDING: { label: "Pending funding" },
  OPEN_POSITIONS: { label: "Open positions" },
  PENDING_REDEMPTIONS: { label: "Pending redemptions" },
  FUNDING_POOL_BALANCE: { label: "Pool balance" },
  FUNDING_IN_FLIGHT: { label: "Funding in flight" },
};

export function obligationKindLabel(kind: string): string {
  return OBLIGATIONS[kind]?.label ?? kind;
}

const CLEARING: Record<string, { label: string }> = {
  CLOSE_OR_MOVE_DESCENDANTS: { label: "Close or move the descendants first" },
  PAY_OUT_OR_REASSIGN: { label: "Pay it out, or reassign it to the parent" },
  CLOSE_ACCOUNTS: { label: "Close the accounts" },
  EMPTY_BALANCE: { label: "Empty the balance" },
  CANCEL_OPEN_ORDERS: { label: "Cancel the open orders" },
  AWAIT_PENDING_OPERATION: { label: "Wait for the pending operation to finish" },
  CLOSE_OR_REDEEM_POSITIONS: { label: "Close or redeem the positions" },
  RETURN_TO_PARENT: { label: "Return the pool to the parent" },
  PAY_OUT_MASTER: { label: "Pay the master out over the rail" },
};

export function clearingActionLabel(action: string): string {
  return CLEARING[action]?.label ?? action;
}

/**
 * The refusal is about the request, not the outcome: broker-service
 * answered and nothing was written. Anything else — a 5xx, a timeout, a
 * dropped connection — leaves the outcome unknown, and the console must
 * say so rather than invite a second submit.
 */
export function outcomeUnknown(e: unknown): boolean {
  if (!e) return false;
  if (e instanceof APIError) return e.status >= 500 || e.status === 0;
  return true;
}

// ── staff ────────────────────────────────────────────────────────────

const STAFF_STATUS: Record<string, { label: string; tone: Tone }> = {
  INVITED: { label: "Invited", tone: "accent" },
  ACTIVE: { label: "Active", tone: "success" },
  SUSPENDED: { label: "Suspended", tone: "warning" },
  REMOVED: { label: "Removed", tone: "muted" },
};

export function staffStatusLabel(status: string): string {
  return STAFF_STATUS[status]?.label ?? status;
}

export function staffStatusTone(status: string): Tone {
  return STAFF_STATUS[status]?.tone ?? "muted";
}

const STAFF_ROLE: Record<string, { label: string }> = {
  OWNER: { label: "Owner" },
  ADMINISTRATOR: { label: "Administrator" },
  FINANCE: { label: "Finance" },
  TRADER: { label: "Trader" },
  VIEWER: { label: "Viewer" },
};

export function staffRoleLabel(role: string): string {
  return STAFF_ROLE[role]?.label ?? role;
}

export interface StaffAbilities {
  suspend: boolean;
  resume: boolean;
  remove: boolean;
  changeRole: boolean;
  /** Email a new set-password link: an invitation whose person has no password yet (SM-968). */
  resendInvite: boolean;
}

/**
 * What an operator holding (or not holding) BROKER_STAFF_MANAGE may do
 * to m — the membership machine (INVITED → ACTIVE ⇄ SUSPENDED, anything
 * but REMOVED → REMOVED, re-role on anything but REMOVED). A CLOSED node
 * takes no staff change at all. broker-service still refuses removing or
 * demoting the last active owner (BROKER_STAFF_LAST_OWNER).
 */
export function staffAbilities(
  m: Pick<StaffMembershipItem, "status" | "account_status">,
  canManage: boolean,
  nodeStatus?: string,
): StaffAbilities {
  const open = canManage && nodeStatus !== "CLOSED" && m.status !== "REMOVED";
  return {
    suspend: open && m.status === "ACTIVE",
    resume: open && m.status === "SUSPENDED",
    remove: open,
    changeRole: open,
    resendInvite: open && isPendingSignUp(m),
  };
}

/** Invited, and the person has not set a password yet: "Pending sign-up" (SM-968). */
export function isPendingSignUp(m: Pick<StaffMembershipItem, "status" | "account_status">): boolean {
  return m.status === "INVITED" && m.account_status === "PENDING";
}

/** A member as the console names them: the staff account's name, else its email, else the raw id (a pre-SM-968 row). */
export function staffMemberLabel(m: Pick<StaffMembershipItem, "display_name" | "email" | "user_id">): string {
  return m.display_name?.trim() || m.email || m.user_id;
}

// ── serviced accounts ────────────────────────────────────────────────

const ACCOUNT_STATUS: Record<string, { label: string; tone: Tone }> = {
  ACTIVE: { label: "Active", tone: "success" },
  SUSPENDED: { label: "Suspended", tone: "warning" },
  CLOSED: { label: "Closed", tone: "muted" },
};

export function accountStatusLabel(status: string): string {
  return ACCOUNT_STATUS[status]?.label ?? status;
}

export function accountStatusTone(status: string): Tone {
  return ACCOUNT_STATUS[status]?.tone ?? "muted";
}

const BALANCE_MODE: Record<string, { label: string }> = {
  SYSTEM_BROKER: { label: "System broker" },
  BROKER_FUNDED: { label: "Broker-funded" },
  THIRD_PARTY_CREDIT: { label: "Third-party credit" },
};

export function balanceModeLabel(mode: string): string {
  return BALANCE_MODE[mode]?.label ?? mode;
}

// ── funding ──────────────────────────────────────────────────────────

const TRANSFER_KIND: Record<string, { label: string }> = {
  POOL_ALLOCATION: { label: "Allocation to a child" },
  POOL_RETURN: { label: "Return to the parent" },
  USER_ALLOCATION: { label: "Allocation to a user" },
  USER_RETURN: { label: "Return from a user" },
};

export function transferKindLabel(kind: string): string {
  return TRANSFER_KIND[kind]?.label ?? kind;
}

const TRANSFER_STATE: Record<string, { label: string; tone: Tone }> = {
  PENDING: { label: "Pending", tone: "warning" },
  COMMITTED: { label: "Committed", tone: "success" },
  FAILED: { label: "Failed", tone: "danger" },
  MANUAL_REVIEW: { label: "Manual review", tone: "danger" },
};

export function transferStateLabel(state: string): string {
  return TRANSFER_STATE[state]?.label ?? state;
}

export function transferStateTone(state: string): Tone {
  return TRANSFER_STATE[state]?.tone ?? "muted";
}

const ACTOR_KIND: Record<string, { label: string }> = {
  BROKER_CREDENTIAL: { label: "Broker server credential" },
  EXCHANGE_ADMIN: { label: "Exchange admin" },
  BROKER_STAFF: { label: "Broker staff" },
};

export function fundingActorLabel(kind: string): string {
  return ACTOR_KIND[kind]?.label ?? kind;
}

// ── reconciliation ───────────────────────────────────────────────────

const FUNDING_RECON: Record<string, { label: string; tone: Tone }> = {
  OK: { label: "Balanced", tone: "success" },
  FINDINGS: { label: "Findings", tone: "warning" },
  FAILED: { label: "Failed", tone: "danger" },
};

export function fundingReconLabel(status: string): string {
  return FUNDING_RECON[status]?.label ?? status;
}

export function fundingReconTone(status: string): Tone {
  return FUNDING_RECON[status]?.tone ?? "muted";
}

const REVENUE_RECON: Record<string, { label: string; tone: Tone }> = {
  RUNNING: { label: "Running", tone: "accent" },
  MATCHED: { label: "Matched", tone: "success" },
  MISMATCH: { label: "Mismatch", tone: "warning" },
  FAILED: { label: "Failed", tone: "danger" },
};

export function revenueReconLabel(state: string): string {
  return REVENUE_RECON[state]?.label ?? state;
}

export function revenueReconTone(state: string): Tone {
  return REVENUE_RECON[state]?.tone ?? "muted";
}

// ── exposure ─────────────────────────────────────────────────────────

const BLOCK_REASONS: Record<string, { label: string }> = {
  HEADROOM_EXHAUSTED: { label: "The headroom is used up" },
  RECEIVABLE_OPEN: { label: "The tree owes the exchange an unresolved receivable" },
  LIFECYCLE: { label: "The node is not active" },
};

export function exposureBlockLabel(reason: string): string {
  return BLOCK_REASONS[reason]?.label ?? reason;
}

// ── integration ──────────────────────────────────────────────────────

const INTEGRATION: Record<string, { label: string; tone: Tone }> = {
  NOT_CONFIGURED: { label: "Not configured", tone: "muted" },
  CONFIGURED: { label: "Configured", tone: "success" },
};

export function integrationStatusLabel(status: string): string {
  return INTEGRATION[status]?.label ?? status;
}

export function integrationStatusTone(status: string): Tone {
  return INTEGRATION[status]?.tone ?? "muted";
}

// ── hierarchy ────────────────────────────────────────────────────────

export interface HierarchyNode {
  broker: BrokerItem;
  children: HierarchyNode[];
}

/**
 * The loaded descendants of rootID as a tree. broker-service pages
 * descendants nearest first — (distance, broker_id) — so every row's
 * parent is on the same or an earlier page and the accumulated pages
 * always nest. A row whose parent is not loaded (the tree changed
 * between two page reads) is returned in `detached` rather than dropped.
 */
export function buildHierarchy(rootID: string, rows: BrokerItem[]): { children: HierarchyNode[]; detached: BrokerItem[] } {
  const nodes = new Map<string, HierarchyNode>();
  for (const b of rows) {
    if (b.broker_id !== rootID && !nodes.has(b.broker_id)) nodes.set(b.broker_id, { broker: b, children: [] });
  }
  const children: HierarchyNode[] = [];
  const detached: BrokerItem[] = [];
  for (const node of nodes.values()) {
    const parent = node.broker.parent_broker_id;
    if (parent === rootID) children.push(node);
    else if (parent && nodes.has(parent)) nodes.get(parent)!.children.push(node);
    else detached.push(node.broker);
  }
  return { children, detached };
}

/** Every descendant under a node of the loaded tree (not only its children). */
export function countLoaded(node: HierarchyNode): number {
  return node.children.reduce((n, c) => n + 1 + countLoaded(c), 0);
}

// ── investigations ───────────────────────────────────────────────────

const SUBJECTS: Record<string, { label: string; placeholder: string }> = {
  USER: { label: "User", placeholder: "user id" },
  BROKER_ACCOUNT: { label: "Broker account", placeholder: "broker account id" },
  ORDER: { label: "Order", placeholder: "order id" },
  TRADE: { label: "Trade", placeholder: "trade id" },
  PAYOUT_BATCH: { label: "Payout batch", placeholder: "payout batch id" },
};

export function subjectKindLabel(kind: string): string {
  return SUBJECTS[kind]?.label ?? kind;
}

export function subjectPlaceholder(kind: string): string {
  return SUBJECTS[kind]?.placeholder ?? "";
}

const STEPS: Record<string, { label: string; tone: Tone }> = {
  ACCOUNT: { label: "Account", tone: "muted" },
  ORDER: { label: "Order", tone: "accent" },
  FILL: { label: "Fill", tone: "accent" },
  FEE: { label: "Fee", tone: "warning" },
  EARNING: { label: "Earning", tone: "success" },
  TRANSFER: { label: "Transfer", tone: "muted" },
  PAYOUT: { label: "Payout", tone: "success" },
};

export function stepKindLabel(kind: string): string {
  return STEPS[kind]?.label ?? kind;
}

export function stepKindTone(kind: string): Tone {
  return STEPS[kind]?.tone ?? "muted";
}

// A step's position along the chain, for steps that share a timestamp —
// backoffice-service's investigateStepRank (the declaration order of
// brokerv1.InvestigationStepKind). A fee is stamped with the time of the
// fill it was charged on (or its order's, when no fill was traced), so
// the tie is the common case, and the kind's NAME would put FEE first.
const STEP_RANK: Record<string, number> = {
  ACCOUNT: 0,
  ORDER: 1,
  FILL: 2,
  FEE: 3,
  EARNING: 4,
  TRANSFER: 5,
  PAYOUT: 6,
};

const stepRank = (kind: string): number => STEP_RANK[kind] ?? Object.keys(STEP_RANK).length;

const byString = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/**
 * The trace in the order backoffice-service composes it (its
 * sortInvestigationSteps): occurred_at, then chain position (Account →
 * Order → Fill → Fee → Earning → Transfer → Payout; a kind this console
 * does not know goes after every known one), then id, then parent_id.
 * Re-applied here (on a copy) so the table never depends on the
 * transport keeping an array's order.
 */
export function orderTraceSteps(steps: InvestigationStep[]): InvestigationStep[] {
  return [...steps].sort(
    (a, b) =>
      a.occurred_at_unix_ms - b.occurred_at_unix_ms ||
      stepRank(a.kind) - stepRank(b.kind) ||
      byString(a.id, b.id) ||
      byString(a.parent_id ?? "", b.parent_id ?? ""),
  );
}

// The gap codes backoffice-service composes a trace with (D63): a trace
// always answers 200 and reports what it could not read here. Anything
// else is the failing upstream's own code — BACKOFFICE_000104 when the
// source did not answer at all.
const GAPS: Record<string, { message: string }> = {
  INVESTIGATE_SUBJECT_UNRESOLVED: {
    message: "The subject could not be matched to a user from recent fills, so the user's other activity is not in this trace.",
  },
  INVESTIGATE_SCAN_BOUNDED: {
    message: "The scan of this source stopped at its limit, so older hops may be missing.",
  },
  INVESTIGATE_PERMISSION_WITHHELD: {
    message: "Your role may not read this part of the trace; the detail names the permission it needs.",
  },
  BACKOFFICE_000104: {
    message: "This source did not answer, so its hops are missing.",
  },
};

/** What a trace gap means, in a sentence an operator can act on. */
export function gapSentence(code: string): string {
  return GAPS[code]?.message ?? "This source answered with an error, so its hops are missing.";
}

/** Where a trace step opens in the console, when it has a page. */
export type TraceTarget =
  | { page: "user"; userId: string }
  | { page: "orders"; userId: string }
  | { page: "trade"; tradeId: string }
  | { page: "broker"; brokerId: string; tab: "accounts" | "funding" }
  | { page: "payout"; batchId: string };

export function traceTarget(s: InvestigationStep): TraceTarget | null {
  switch (s.kind) {
    case "ACCOUNT":
      if (s.broker_id) return { page: "broker", brokerId: s.broker_id, tab: "accounts" };
      return s.user_id ? { page: "user", userId: s.user_id } : null;
    case "ORDER":
      return s.user_id ? { page: "orders", userId: s.user_id } : null;
    case "FILL":
      return s.id ? { page: "trade", tradeId: s.id } : null;
    case "TRANSFER":
      return s.broker_id ? { page: "broker", brokerId: s.broker_id, tab: "funding" } : null;
    case "PAYOUT":
      return s.id ? { page: "payout", batchId: s.id } : null;
    default:
      // FEE and EARNING have no page of their own in this console; their
      // parent (the fill, the payout) carries the link.
      return null;
  }
}

// SM-978 / SM-984: broker-service audit actions and kinds that read better
// in words than as codes. Anything not listed is shown as its code.
const BROKER_AUDIT_ACTION_LABEL: Record<string, string> = {
  BROKER_CURRENCY_IMAGE_SET: "Currency image changed",
  BROKER_CURRENCY_IMAGE_REMOVED: "Currency image removed",
};
const BROKER_AUDIT_ENTITY_LABEL: Record<string, string> = {
  CURRENCY_IMAGE: "Currency image",
};

/** A broker audit row's action in plain words, or its code when it has none. */
export function brokerAuditActionLabel(action: string): string {
  return BROKER_AUDIT_ACTION_LABEL[action] ?? action;
}

/** A broker audit row's entity kind in plain words, or its code. */
export function brokerAuditEntityLabel(kind: string): string {
  return BROKER_AUDIT_ENTITY_LABEL[kind] ?? kind;
}
